/**
 * Worklist React Query Hooks
 *
 * TanStack Query hooks for worklist operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  WorklistFilters,
  CreateWorklistItemData,
  UpdateWorklistItemData,
} from '@/domain/worklist'
import type { WorklistStatus } from '@/config/constants'
import { useAuth, usePermiso } from '@/application/auth'
import * as worklistService from './worklist.service'

// =============================================================================
// Query Keys
// =============================================================================

export const worklistKeys = {
  all: ['worklist'] as const,
  lists: () => [...worklistKeys.all, 'list'] as const,
  list: (filters?: WorklistFilters) => [...worklistKeys.lists(), filters] as const,
  details: () => [...worklistKeys.all, 'detail'] as const,
  detail: (id: string) => [...worklistKeys.details(), id] as const,
  my: (status?: WorklistStatus) => [...worklistKeys.all, 'my', status] as const,
  overdue: () => [...worklistKeys.all, 'overdue'] as const,
  unassigned: () => [...worklistKeys.all, 'unassigned'] as const,
  returned: () => [...worklistKeys.all, 'returned'] as const,
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Cada consulta declara el permiso que EXIGE su endpoint.
 *
 * Estaban cerradas solo por "hay sesion", que no es la pregunta: el servidor
 * no pide estar autenticado, pide un permiso concreto. Un tecnologo abria el
 * panel y disparaba tres llamadas a la cola de trabajo que le devolvian 403,
 * y el usuario veia bloques vacios sin explicacion.
 *
 * Va aqui, en la capa de datos, y no en cada pantalla: la capa de datos es la
 * unica que sabe a que endpoint llama. Cualquier pantalla que use estos
 * hooks queda cubierta sin que nadie se acuerde de gatearla.
 *
 * Los permisos son los mismos que exigen las rutas del backend
 * (worklist.routes.ts). No sustituyen a esa comprobacion: evitan pedir lo que
 * se va a negar.
 */

/**
 * Get paginated/filtered list of worklist items
 */
export function useWorklist(filters?: WorklistFilters) {
  const { isAuthenticated } = useAuth()
  const puede = usePermiso('worklist:view:all')
  return useQuery({
    queryKey: worklistKeys.list(filters),
    queryFn: () => worklistService.getWorklistItems(filters),
    enabled: isAuthenticated && puede,
    staleTime: 1000 * 30, // 30 seconds - worklist changes frequently
  })
}

/**
 * Get a single worklist item by ID
 */
export function useWorklistItem(id: string | undefined) {
  const puede = usePermiso('worklist:view:all')
  return useQuery({
    queryKey: worklistKeys.detail(id!),
    queryFn: () => worklistService.getWorklistItemById(id!),
    enabled: !!id && puede,
    staleTime: 1000 * 30,
  })
}

/**
 * Get current user's worklist
 */
export function useMyWorklist(status?: WorklistStatus) {
  const { isAuthenticated } = useAuth()
  const puede = usePermiso('worklist:view:own')
  return useQuery({
    queryKey: worklistKeys.my(status),
    queryFn: () => worklistService.getMyWorklist(status),
    enabled: isAuthenticated && puede,
    staleTime: 1000 * 30,
  })
}

/**
 * Get overdue worklist items
 */
export function useOverdueItems() {
  const { isAuthenticated } = useAuth()
  const puede = usePermiso('worklist:view:all')
  return useQuery({
    queryKey: worklistKeys.overdue(),
    queryFn: () => worklistService.getOverdueItems(),
    enabled: isAuthenticated && puede,
    staleTime: 1000 * 60, // 1 minute
  })
}

/**
 * Get unassigned worklist items
 * For admin/technician to see what needs assignment
 */
export function useUnassignedItems(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth()
  const { enabled = true } = options || {}
  const puede = usePermiso('worklist:manage')
  return useQuery({
    queryKey: worklistKeys.unassigned(),
    queryFn: () => worklistService.getUnassignedItems(),
    enabled: isAuthenticated && enabled && puede,
    staleTime: 1000 * 30, // 30 seconds
  })
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new worklist item
 */
export function useCreateWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateWorklistItemData) => worklistService.createWorklistItem(data),
    onSuccess: (newItem) => {
      // Invalidate all worklist queries
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
      // Add to cache
      queryClient.setQueryData(worklistKeys.detail(newItem.id), newItem)
    },
  })
}

/**
 * Update a worklist item
 */
export function useUpdateWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorklistItemData }) =>
      worklistService.updateWorklistItem(id, data),
    onSuccess: (updatedItem) => {
      // Update cache
      queryClient.setQueryData(worklistKeys.detail(updatedItem.id), updatedItem)
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.overdue() })
    },
  })
}

/**
 * Start working on a worklist item
 */
export function useStartWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => worklistService.startWorklistItem(id),
    onSuccess: (updatedItem) => {
      queryClient.setQueryData(worklistKeys.detail(updatedItem.id), updatedItem)
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
    },
  })
}

/**
 * Complete a worklist item
 */
export function useCompleteWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => worklistService.completeWorklistItem(id),
    onSuccess: (updatedItem) => {
      queryClient.setQueryData(worklistKeys.detail(updatedItem.id), updatedItem)
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.overdue() })
    },
  })
}

/**
 * Cancel a worklist item
 */
export function useCancelWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => worklistService.cancelWorklistItem(id),
    onSuccess: (updatedItem) => {
      queryClient.setQueryData(worklistKeys.detail(updatedItem.id), updatedItem)
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.overdue() })
    },
  })
}

/**
 * Delete a worklist item
 */
export function useDeleteWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => worklistService.deleteWorklistItem(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: worklistKeys.detail(deletedId) })
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.overdue() })
    },
  })
}

/**
 * Get worklist item for a specific study
 * Used to auto-start when opening the viewer
 */
export function useWorklistItemByStudy(studyId: string | undefined) {
  const puede = usePermiso('worklist:view:all')
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: [...worklistKeys.all, 'byStudy', studyId],
    queryFn: async () => {
      const result = await worklistService.getWorklistItems({ studyId, limit: 1 })
      return result.items[0] || null
    },
    // Consulta /worklist, que exige worklist:view:all. El medico remitente
    // abre el visor y no lo tiene: le devolvia 403 en cada apertura.
    enabled: isAuthenticated && !!studyId && puede,
    staleTime: 1000 * 30,
  })
}

/**
 * Auto-start worklist item when opening viewer
 * Returns mutation that auto-starts if the item is PENDING
 */
export function useAutoStartWorklist() {
  const startMutation = useStartWorklistItem()

  const autoStart = async (worklistItem: import('@/domain/worklist').WorklistItem | null) => {
    if (!worklistItem) return null
    if (worklistItem.status !== 'PENDING') return worklistItem

    try {
      const updated = await startMutation.mutateAsync(worklistItem.id)
      return updated
    } catch {
      // If failed, return original
      return worklistItem
    }
  }

  return { autoStart, isPending: startMutation.isPending }
}

/**
 * Assign a worklist item to a radiologist
 */
export function useAssignWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: worklistService.AssignWorklistData }) =>
      worklistService.assignWorklistItem(id, data),
    onSuccess: (updatedItem) => {
      // Update cache
      queryClient.setQueryData(worklistKeys.detail(updatedItem.id), updatedItem)
      // Invalidate all relevant lists
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.unassigned() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.overdue() })
    },
  })
}

/**
 * Return a worklist item with a reason
 */
export function useReturnWorklistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      worklistService.returnWorklistItem(id, reason),
    onSuccess: (updatedItem) => {
      queryClient.setQueryData(worklistKeys.detail(updatedItem.id), updatedItem)
      queryClient.invalidateQueries({ queryKey: worklistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.my() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.overdue() })
      queryClient.invalidateQueries({ queryKey: worklistKeys.returned() })
    },
  })
}

/**
 * Get returned worklist items
 */
export function useReturnedItems() {
  const { isAuthenticated } = useAuth()
  const puede = usePermiso('worklist:view:all')
  return useQuery({
    queryKey: worklistKeys.returned(),
    queryFn: () => worklistService.getReturnedItems(),
    enabled: isAuthenticated && puede,
    staleTime: 1000 * 30,
  })
}
